import type { BaseQueryFn, FetchArgs } from '@reduxjs/toolkit/query/react';
import downloadjs from 'downloadjs';
import type { ExtendedError } from 'factories/api';
import Cookies from 'js-cookie';
import { bootstrappedData } from 'utils/bootstrap';
import * as domReader from 'utils/dom-reader';
import HttpStatusCode from './httpStatusCode';
import 'types/global';

const downloadableMimeTypes = ['application/pdf', 'application/vnd.ms-excel', 'text/csv'];

/**
 * Strips UTF-8 BOM (Byte Order Mark) from the beginning of a string
 * BOM can cause JSON parsing errors when present at the start of a response
 */
const stripBOM = (text: string): string => {
  // UTF-8 BOM is U+FEFF
  if (text.charCodeAt(0) === 0xfeff) {
    return text.slice(1);
  }
  return text;
};

export interface Options {
  host?: string;
  accountPrefix?: boolean;
  rawUrl?: boolean;
  noAuth?: boolean;
  isFile?: boolean;
  filename?: string;
  abortSignal?: AbortSignal;
  token?: string;
}

const handleErrors = (response: Response): Promise<Response> => {
  if (response.ok) {
    return Promise.resolve(response);
  }

  // Error out for Internal Server Error instead of
  // attempting to parse as JSON
  if (response.status === HttpStatusCode.INTERNAL_SERVER_ERROR) {
    const error: ExtendedError = new Error(response.statusText || 'Internal Server Error');
    error.response = response.statusText;
    error.requestInfo = `${response.type} ${response.url}`;
    error.reason = 'server-error';
    error.statusCode = response.status;
    throw error;
  }

  if (response.status === HttpStatusCode.UNAUTHORIZED) {
    if (window.location.pathname !== '/login') {
      window.location.assign('/login');
    }
    const error: ExtendedError = new Error('Unauthorized');
    error.response = response.statusText;
    error.requestInfo = `${response.type} ${response.url}`;
    error.reason = 'unauthorized';
    error.statusCode = response.status;
    throw error;
  }

  if (response.status === HttpStatusCode.GATEWAY_TIMEOUT) {
    const error: ExtendedError = new Error('Request timed out');
    error.response = response.statusText;
    error.requestInfo = `${response.type} ${response.url}`;
    error.reason = 'timeout';
    error.statusCode = response.status;
    throw error;
  }

  return response
    .clone()
    .json()
    .catch(() => response.text())
    .then((errorResponse: unknown | string) => {
      const error: ExtendedError = new Error(
        response.statusText || `Unknown Error: HTTP ${response.status} (${response.url})`
      );
      error.response = errorResponse;
      error.rawResponse = JSON.stringify(errorResponse);
      error.requestInfo = `${response.type} ${response.url}`;
      error.reason = 'server-error';
      error.statusCode = response.status;
      throw error;
    });
};

const handleResponse = async (response: Response, options: Options): Promise<ApiResponse<any>> => {
  const responseData: ApiResponse<any> = {
    data: undefined,
    responseCode: response.status,
    responseHeaders: response.headers,
    getPaginationHeaders: () => {
      const totalCount = response.headers.get('X-Total-Count');
      if (totalCount == null) {
        throw Error('Missing expected pagination headers');
      }
      return {
        totalCount: Number(totalCount),
      };
    },
  };

  if (response.status === HttpStatusCode.NO_CONTENT) {
    return Promise.resolve(responseData);
  }

  if (response.status >= HttpStatusCode.INTERNAL_SERVER_ERROR) {
    return Promise.reject();
  }

  const contentType = response.headers.get('content-type');

  // Handle Report downloads in PDF or XLS format
  if (
    (contentType != null && downloadableMimeTypes.includes(contentType)) ||
    (options.isFile && !/application\/json/i.test(contentType!))
  ) {
    const blob = await response.blob();
    return {
      ...responseData,
      data: blob,
    };
  }

  const parsed = contentType?.startsWith('application/json')
    ? response.text().then(text => {
        try {
          return JSON.parse(stripBOM(String(text ?? '')));
        } catch (e) {
          // Log the error details for debugging but don't expose raw parsing errors to users
          console.error(`Error parsing JSON response from ${response.url}:`, e);
          throw new Error(`Server responded with invalid JSON: ${response.url} - ${e}`);
        }
      })
    : response.text();

  return {
    ...responseData,
    data: await parsed,
  };
};

const handleDownload = (
  response: ApiResponse<any>,
  options: Options
): Promise<ApiResponse<any>> => {
  if (response.data instanceof Blob) {
    downloadjs(response.data, options.filename);
  }
  return Promise.resolve(response);
};

/**
 * A request body: a JSON string, or `FormData` for a multipart upload (the
 * browser then sets the multipart `Content-Type` with its boundary itself).
 */
export type RequestBody = null | string | FormData;

export const makeRequest = <T = any>(
  url: string,
  method: string,
  data: RequestBody,
  options: Options = {}
): Promise<T> => {
  return makeRequestAdvanced(url, method, data, options).then(response => response.data);
};

export interface PaginationHeaders {
  totalCount: number;
}

export interface ApiResponse<T> {
  data: T;
  responseCode: number;
  responseHeaders: Headers;
  getPaginationHeaders: () => PaginationHeaders;
}

const API_V1_2_PATTERNS = [
  'user_account_settings',
  'users/summary',
  'reports',
  'shared_reports.json',
  'projects.json',
  'add_ons',
];

export const determineApiEndpointVersion = (url: string, method?: string): '1.1' | '1.2' => {
  if (url.includes('shared_reports/') && method && /GET/i.test(method)) {
    return '1.1';
  }

  if (API_V1_2_PATTERNS.some(pattern => url.includes(pattern))) {
    return '1.2';
  }

  return '1.1';
};

interface BuiltRequest {
  requestUrl: string;
  request: RequestInit;
  headers: Headers;
}

/**
 * The URL, headers, credentials and CSRF token every Timely request shares.
 * `makeRequestAdvanced` and `makeStreamingRequest` both build from here so the
 * two cannot drift on auth.
 */
const buildRequest = (
  url: string,
  method: string,
  data: RequestBody,
  options: Options = {}
): BuiltRequest => {
  const prefix: number = bootstrappedData.account.id;
  const baseHost = options.host || bootstrappedData.host;

  const appIsStandalone = !!bootstrappedData?.standaloneClient;
  const useBearerTokenForAuth = !!(appIsStandalone || options.token);
  const apiVersion = determineApiEndpointVersion(url, method);

  const host = appIsStandalone ? `${baseHost}/${apiVersion}` : baseHost;
  let requestUrl = options.accountPrefix === false ? `${host}/${url}` : `${host}/${prefix}/${url}`;
  if (options.rawUrl) {
    requestUrl = url;
  }

  // options.isFile works with these headers in Chrome, do we need to adjust?

  const headers: Headers = useBearerTokenForAuth
    ? new Headers({
        Accept: 'application/json',
        Authorization: `Bearer ${options.token || Cookies.get('token') || 'invalid-bearer'}`,
        'Content-Type': 'application/json',
        // Ref https://pusher.com/docs/server_api_guide/server_excluding_recipients#problem
        // see src/index.js for initialization
        'TL-Socket-Id': window.TimelyPusherSocketId || '',
        'sec-fetch-mode': 'cors',
        'sec-fetch-site': 'cross-site',
      })
    : new Headers({
        Accept: 'application/json',
        'Content-Type': 'application/json',
        // Ref https://pusher.com/docs/server_api_guide/server_excluding_recipients#problem
        // see src/index.js for initialization
        'TL-Socket-Id': window.TimelyPusherSocketId || '',
      });

  if (options.noAuth) {
    headers.delete('Authorization');
    headers.delete('TL-Socket-Id');
  }

  const request: RequestInit = {
    // Send the HTTP method UPPERCASE to fetch: the Fetch spec normalizes GET/POST/PUT/DELETE/HEAD/
    // OPTIONS to uppercase but NOT PATCH, so a lowercased `patch` (the adapter lowercases methods for
    // the `method !== 'get'` checks below) would go out verbatim and miss Rails' case-sensitive PATCH
    // routes — a 400 with no controller log. No-op for the already-normalized verbs; keeps the
    // lowercase `method` local for the CSRF/body guard below.
    method: method.toUpperCase(),
    headers,
    mode: useBearerTokenForAuth ? 'cors' : 'same-origin',
    credentials: 'same-origin',
    signal: options.abortSignal,
  };

  // Some PUT/POST/DELETE's require Rail's Cross-Site Request Forgery prevention:
  if (method !== 'get') {
    const csrfToken = domReader.getMetaTagName('csrf-token');
    if (!appIsStandalone && csrfToken) {
      headers.append('X-CSRF-Token', csrfToken);
    }
    request.body = data;
  }

  // A multipart body carries its own boundary in the Content-Type; fetch only
  // fills that in when no Content-Type is set, so drop the JSON default.
  if (data instanceof FormData) {
    headers.delete('Content-Type');
  }

  return { requestUrl, request, headers };
};

export const makeRequestAdvanced = <T = any>(
  url: string,
  method: string,
  data: RequestBody,
  options: Options = {}
): Promise<ApiResponse<T>> => {
  const { requestUrl, request } = buildRequest(url, method, data, options);

  return fetch(requestUrl, request)
    .then(handleErrors)
    .then(res => handleResponse(res, options))
    .then(res => handleDownload(res, options));
};

/**
 * Alias of `fetch` /w same argument structure, but runs through `makeRequestAdvanced`
 * Useful for `TicContextProvider`s `fetch` usage, without duplicating Timely-specific logic
 */
export const customFetchish = async <T = any>(
  url: RequestInfo | URL,
  options: RequestInit | undefined
): Promise<T> => {
  const { method = 'GET', body } = options || {};
  const data = body ? JSON.stringify(body) : null;
  const res = await makeRequestAdvanced(url as string, method, data, { rawUrl: true });
  return res as T;
};

/**
 * Opens a server-sent events response with the same URL, cookie and CSRF
 * handling as `makeRequestAdvanced`, and resolves with the raw body stream for
 * the caller's SSE parser. Differences from a JSON request:
 * - `Accept: text/event-stream`;
 * - no `TL-Socket-Id`, so Pusher does not exclude this browser from events
 *   about writes the agent makes on its behalf;
 * - a 404 whose body is the bare `Unauthorized` text (Rails' CSRF rejection
 *   on this route) is reported as `unauthorized`, not as a generic server
 *   error; 401 still redirects to login through `handleErrors`;
 * - an ok response that is not an event stream is rejected before any read.
 */
export const makeStreamingRequest = async (
  url: string,
  method: string,
  data: null | string,
  options: Options = {}
): Promise<ReadableStream<Uint8Array>> => {
  const { requestUrl, request, headers } = buildRequest(url, method, data, options);
  headers.set('Accept', 'text/event-stream');
  headers.delete('TL-Socket-Id');

  const response = await fetch(requestUrl, request);

  if (response.status === HttpStatusCode.NOT_FOUND) {
    const body = await response.clone().text();
    if (body.trim() === 'Unauthorized') {
      const error: ExtendedError = new Error('Unauthorized');
      error.response = body;
      error.requestInfo = `${response.type} ${response.url}`;
      error.reason = 'unauthorized';
      error.statusCode = response.status;
      throw error;
    }
  }

  await handleErrors(response);

  const contentType = response.headers.get('content-type') ?? '';
  if (!/^text\/event-stream/i.test(contentType) || response.body == null) {
    const error: ExtendedError = new Error(
      `Expected an event stream from ${response.url}, got ${contentType || 'no content type'}`
    );
    error.requestInfo = `${response.type} ${response.url}`;
    error.reason = 'server-error';
    error.statusCode = response.status;
    throw error;
  }

  return response.body;
};

type TimelyBaseQueryArgs = string | FetchArgs;

/**
 * Response metadata handed to an endpoint's `transformResponse`, for the parts of a response that
 * live outside its body (e.g. the `X-Total-Count` header of a paginated list).
 */
export interface TimelyBaseQueryMeta {
  responseHeaders: Headers;
}

interface TimelyBaseQueryOptions {
  accountPrefix?: boolean;
  prepareUrl?: (url: string) => string;
}

const appendQueryParams = (url: string, params?: FetchArgs['params']): string => {
  if (!params) return url;

  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value == null) continue;

    if (Array.isArray(value)) {
      for (const item of value) {
        if (item != null) searchParams.append(key, String(item));
      }
    } else {
      searchParams.append(key, String(value));
    }
  }

  const query = searchParams.toString();
  return query ? `${url}${url.includes('?') ? '&' : '?'}${query}` : url;
};

const getRequestBody = (body: FetchArgs['body']): string | null => {
  if (body == null) return null;
  if (typeof body === 'string') return body;

  return JSON.stringify(body);
};

/**
 * Creates a baseQuery function for RTK Query that uses Timely's standard auth.
 * Handles standalone mode (bearer token + CORS) vs normal mode (session cookies + CSRF).
 */
export function createTimelyBaseQuery(
  options: TimelyBaseQueryOptions = {}
): BaseQueryFn<TimelyBaseQueryArgs, unknown, unknown, {}, TimelyBaseQueryMeta> {
  const { accountPrefix = true, prepareUrl = url => url } = options;

  return async (args, api) => {
    const requestArgs: FetchArgs = typeof args === 'string' ? { url: args } : args;
    const url = appendQueryParams(prepareUrl(requestArgs.url), requestArgs.params);
    const method = (requestArgs.method ?? 'GET').toLowerCase();

    try {
      const response = await makeRequestAdvanced(url, method, getRequestBody(requestArgs.body), {
        accountPrefix,
        abortSignal: api.signal,
      });

      return { data: response.data, meta: { responseHeaders: response.responseHeaders } };
    } catch (error) {
      return { error };
    }
  };
}
